import { VISIBLE_STATUSES } from '@/config/content';
import type { LevelId, Locale, SchoolType } from '@/domain/ids';
import { checkPackIntegrity, type ContentIssue } from './integrity';
import { tnPrimaryPack } from './packs/tn-primary';
import {
  curriculumPackSchema,
  type ContentItem,
  type CurriculumPack,
  type LearningObjective,
  type Lesson,
  type Localized,
  type SchoolLevel,
  type SchoolVariant,
  type Subject,
} from './schema';

export class ContentError extends Error {
  readonly issues: ContentIssue[];
  constructor(message: string, issues: ContentIssue[]) {
    super(`${message}\n${issues.map((i) => `- ${i.path}: ${i.message}`).join('\n')}`);
    this.name = 'ContentError';
    this.issues = issues;
  }
}

/** Parses and checks a content pack; throws a ContentError listing every problem. */
export function loadPack(raw: unknown): CurriculumPack {
  const parsed = curriculumPackSchema.safeParse(raw);
  if (!parsed.success) {
    throw new ContentError(
      'Invalid curriculum pack',
      parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
    );
  }
  const issues = checkPackIntegrity(parsed.data);
  if (issues.length > 0) throw new ContentError('Inconsistent curriculum pack', issues);
  return parsed.data;
}

export function localize(text: Localized, locale: Locale): string {
  return text[locale] ?? text.ar;
}

/** Read-only queries over a validated pack. UI and engines never read the raw pack. */
export class Curriculum {
  readonly pack: CurriculumPack;
  private readonly subjects: Map<string, Subject>;
  private readonly objectives: Map<string, LearningObjective>;
  private readonly lessons: Map<string, Lesson>;
  private readonly itemsByObjective: Map<string, ContentItem[]>;
  private readonly levels: Map<LevelId, SchoolLevel>;

  constructor(pack: CurriculumPack) {
    this.pack = pack;
    this.subjects = new Map(pack.subjects.map((s) => [s.id, s]));
    this.objectives = new Map(pack.objectives.map((o) => [o.id, o]));
    this.lessons = new Map(pack.lessons.map((l) => [l.objectiveId, l]));
    this.levels = new Map(pack.levels.map((l) => [l.id, l]));
    this.itemsByObjective = new Map();
    for (const item of pack.items) {
      if (!VISIBLE_STATUSES.includes(item.status)) continue;
      const list = this.itemsByObjective.get(item.objectiveId) ?? [];
      list.push(item);
      this.itemsByObjective.set(item.objectiveId, list);
    }
  }

  get versionId() {
    return this.pack.curriculumVersion.id;
  }

  allLevels(): SchoolLevel[] {
    return [...this.pack.levels].sort((a, b) => a.order - b.order);
  }

  level(id: LevelId): SchoolLevel | undefined {
    return this.levels.get(id);
  }

  variantsFor(schoolType: SchoolType): SchoolVariant[] {
    return this.pack.variants.filter(
      (v) => VISIBLE_STATUSES.includes(v.status) && v.schoolTypes.includes(schoolType),
    );
  }

  variant(id: string): SchoolVariant | undefined {
    return this.pack.variants.find((v) => v.id === id);
  }

  subject(id: string): Subject | undefined {
    return this.subjects.get(id);
  }

  objective(id: string): LearningObjective | undefined {
    return this.objectives.get(id);
  }

  lesson(objectiveId: string): Lesson | undefined {
    const lesson = this.lessons.get(objectiveId);
    return lesson && VISIBLE_STATUSES.includes(lesson.status) ? lesson : undefined;
  }

  items(objectiveId: string): ContentItem[] {
    return this.itemsByObjective.get(objectiveId) ?? [];
  }

  private isVisible(objective: LearningObjective, variantId: string): boolean {
    return (
      VISIBLE_STATUSES.includes(objective.status) &&
      (!objective.variants || objective.variants.includes(variantId))
    );
  }

  subjectsFor(levelId: LevelId): Subject[] {
    return this.pack.subjects.filter(
      (s) => VISIBLE_STATUSES.includes(s.status) && s.levels.includes(levelId),
    );
  }

  /** Objectives of a subject for the learner's level, in learning-map order. */
  objectivesFor(subjectId: string, levelId: LevelId, variantId: string): LearningObjective[] {
    return this.pack.objectives
      .filter(
        (o) =>
          o.subjectId === subjectId && o.levels.includes(levelId) && this.isVisible(o, variantId),
      )
      .sort((a, b) => a.order - b.order);
  }

  /** Objectives from earlier years, offered for revision. */
  earlierObjectives(subjectId: string, levelId: LevelId, variantId: string): LearningObjective[] {
    const current = this.level(levelId)?.order ?? 0;
    return this.pack.objectives
      .filter(
        (o) =>
          o.subjectId === subjectId &&
          !o.levels.includes(levelId) &&
          o.levels.every((l) => (this.level(l)?.order ?? 99) < current) &&
          this.isVisible(o, variantId),
      )
      .sort((a, b) => a.order - b.order);
  }

  /** Every objective a learner of this level can see, across subjects. */
  objectivesForLevel(levelId: LevelId, variantId: string): LearningObjective[] {
    return this.subjectsFor(levelId).flatMap((s) => this.objectivesFor(s.id, levelId, variantId));
  }
}

/** The active curriculum. Loading validates the pack, so bad content fails fast. */
export const curriculum = new Curriculum(loadPack(tnPrimaryPack));
